import { matchPath } from 'react-router'
import parser from 'tld-extract'

import store from '../store'
import history from '../history'
import { keys, reduce } from './common'

import { setSelectedPlatform } from '../modules/actions/app'
import { SELECTED_PARTNERSHIP_ID } from '../modules/actions/partnerships'

import { ROUTE_PARAMS, ROUTES } from '../constants/routes'

export const getParameterFromUrl = parameterKey => {
  const { [parameterKey]: parameterValue } = createJsonFromQueryString(window.location.search)
  return parameterValue
}

export const getParameterFromPath = (parameterKey, path) => {
  const parsedPath = parsePathFromRoutes(path)
  const parameter = parsedPath.params && parsedPath.params[parameterKey]

  // avoid sending route parameter id i.e. -> :adAccount
  return parameter === ':' + parameterKey ? '' : parameter
}

const parsePathFromRoutes = path => {
  // retrieve path parameters like platform, adAccount, and etc..
  let match = undefined

  keys(ROUTES).forEach(route => {
    // search matching for current route through ROUTES list to retrieve params based on that
    const matched = matchPath(
      {
        path: ROUTES[route],
        exact: true,
        strict: false
      },
      path || window.location.pathname
    )
    if (matched) {
      match = matched
    }
  })

  return match || {}
}

export const getCurrentRoute = () => {
  const matchedRoute = parsePathFromRoutes().pattern

  if (matchedRoute && matchedRoute.path) {
    return matchedRoute.path
  } else {
    return undefined
  }
}

export function replaceRouteParams(route, params) {
  return reduce(keys(params), (acc, cur) => acc.replace(`:${cur}`, params[cur]), route)
}

export function updateRouteParams(newParams) {
  // replace only new parameters in path
  const currentRoute = getCurrentRoute()

  if (currentRoute) {
    // don't loose query params on routeParams update
    const routeWithSearch = currentRoute + window.location.search
    updateParams(routeWithSearch, newParams)
  }
}

function updateParams(route, newParams) {
  const currentParams = parsePathFromRoutes().params

  history.push(
    replaceRouteParams(route, {
      ...currentParams,
      ...newParams
    })
  )
}

export function checkIfParamsRelatedLink(url = '') {
  return url.indexOf('/:') >= 0
}

export function createJsonFromQueryString(queryString, decode = true) {
  let parsedObject = {}
  let incomingQuery = queryString.substr(1)
  if (incomingQuery !== '') {
    let jsonString = ''
    const queryParamsList = incomingQuery.split('&')

    for (let i = 0; i < queryParamsList.length; i++) {
      const paramKeyValue = queryParamsList[i].split('=')
      if (paramKeyValue.length > 1 && paramKeyValue[0] && paramKeyValue[1]) {
        const key = paramKeyValue[0]
        const value = paramKeyValue[1]

        if (decode) {
          jsonString += `"${decode ? decodeURIComponent(key) : key}":"${decode ? decodeURIComponent(value) : value}",`
        } else {
          // skip decoding and JSON creation - just save values to object directly
          parsedObject[key] = value
        }
      }
    }

    if (decode) {
      parsedObject = JSON.parse(`{${jsonString.substr(0, jsonString.length - 1)}}`)
    }
  }
  return parsedObject
}

export function redirectTo(url, newParams) {
  if (checkIfParamsRelatedLink(url)) {
    // copy existed params for ParamsRelatedLink + adjust with new
    // it will populate only params from pathname
    // so if some parameter is missing it will not be populated from redux
    updateParams(url, newParams)

    if (newParams) {
      // manage app platform parameter during route switch
      // trigger parameters change only when new params exists and differs from selected
      const { app } = store.getState()
      const currentPlatform = app.selectedPlatform

      const isDifferentPlatform =
        newParams[ROUTE_PARAMS.platform] && currentPlatform !== newParams[ROUTE_PARAMS.platform]

      if (isDifferentPlatform) {
        // set new platform to reducer if platforms changes during redirect
        store.dispatch(setSelectedPlatform(newParams[ROUTE_PARAMS.platform]))
      }
    }
  } else {
    history.push(url)
  }
}

export function checkIfPlatformRelatedLink(url = '') {
  return url.indexOf(`/:${ROUTE_PARAMS.platform}/`) >= 0
}

export function checkIfAdAccountRelatedLink(url = '') {
  return url.indexOf(`/:${ROUTE_PARAMS.adAccount}/`) >= 0
}

export function checkIfSelfAccountRelatedLink(url = '') {
  return url.indexOf(`/:${ROUTE_PARAMS.selfAccount}/`) >= 0
}

export function getDefaultPageUrl(selectedPartnership) {
  const { partnerships } = store.getState()
  const { display_rights: displayRights, display_sroi: displaySroi } = selectedPartnership

  if (!displayRights && displaySroi) {
    return replaceRouteParams(ROUTES.socialReturnOnInvestment, {
      [ROUTE_PARAMS.partnership]: partnerships[SELECTED_PARTNERSHIP_ID]
    })
  } else {
    return replaceRouteParams(ROUTES.partnerships, {
      [ROUTE_PARAMS.partnership]: partnerships[SELECTED_PARTNERSHIP_ID]
    })
  }
}

export function isValidHttpUrl(string) {
  return /^(?:\w+:)?\/\/([^\s.]+\.\S{2}|localhost[:?\d]*)\S*$/.test(string)
}

export const getUrlDomain = string => {
  if (isValidHttpUrl(string)) {
    try {
      const url = new URL(string)
      return parser(url).domain
    } catch (e) {
      return ''
    }
  } else {
    return ''
  }
}

export const clearUrlSearch = string => {
  if (isValidHttpUrl(string)) {
    try {
      const urlObject = new URL(string)
      urlObject.search = ''
      // cleared url string
      return urlObject.toString()
    } catch (e) {
      return ''
    }
  } else {
    return ''
  }
}
